import {
  ArrowTopRightOnSquareIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EnvelopeIcon,
  InformationCircleIcon,
  KeyIcon,
  SparklesIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  ArrowDownTrayIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/solid";
import { useQuery } from "@tanstack/react-query";
import axios, { AxiosError, AxiosResponse } from "axios";
import { useContext, useEffect, useState } from "react";
import ReactGA from "react-ga4";
import Plot from "react-plotly.js";
import { Link, Navigate } from "react-router-dom";

import UsageDetailCard from "./HubUsageDetail";
import RefillSettingsCard, {
  ConvertToSubscriptionMetadata,
} from "./RefillSettingsForm";
import { SubscriptionMetadata } from "./RefillSettingsForm";
import RouteInsights from "./RouteInsights";

import { useApiKey } from "context/ApiKeyContext";
import UserContext from "context/UserContext";

import Badge from "components/atoms/Badge";
import Button from "components/atoms/Button";
import Card from "components/atoms/Card";
import Spinner from "components/atoms/Spinner";
import { convertToNumLetter } from "components/molecules/PriceTables/PriceTableNutritionAPI";
import SlackSignUp from "components/molecules/SlackSignUp";

import { error, success } from "utils";
import { ProductDataType, UsageHistoryReportForPeriod } from "utils/types";

const TIERS = [
  {
    name: "Starter",
    slug: "nutrition-platform-starter",
    price: "$99",
    priceNum: 99,
    tokens: "1M",
    refillPrice: "$25 / 1M",
    refillPricePerM: 25,
    features: {
      easyAI: true,
      localization: true,
      modelSwitching: false,
      insights: false,
      aiInsights: false,
      support: "Email + Community Slack",
    },
    highlight: false,
  },
  {
    name: "Growth",
    slug: "nutrition-platform-growth",
    price: "$599",
    priceNum: 599,
    tokens: "5M",
    refillPrice: "$10 / 1M",
    refillPricePerM: 10,
    features: {
      easyAI: true,
      localization: true,
      modelSwitching: true,
      insights: true,
      aiInsights: false,
      support: "Dedicated Slack Channel",
    },
    highlight: true,
  },
  {
    name: "Pro",
    slug: "nutrition-platform-pro",
    price: "$2,999",
    priceNum: 2999,
    tokens: "100M",
    refillPrice: "$2.50 / 1M",
    refillPricePerM: 2.5,
    features: {
      easyAI: true,
      localization: true,
      modelSwitching: true,
      insights: true,
      aiInsights: true,
      support: "Enterprise Support",
    },
    highlight: false,
  },
];

const GLOBAL_ROUTE_AVG_DAYS = 30;

type RouteAverageRow = {
  category: string;
  totalCalls: number;
  avgBilledTokens: number;
  totalBilledTokens: number;
};

type RawRouteAverageRow = {
  category?: string;
  totalCalls?: number | string;
  avgBilledTokens?: number | string;
  totalBilledTokens?: number | string;
  total_calls?: number | string;
  avg_billed_tokens?: number | string;
  total_billed_tokens?: number | string;
};

const toNumber = (v: unknown): number => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
};

const normalizeRouteAverageRows = (raw: unknown): RouteAverageRow[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => row as RawRouteAverageRow)
    .map((row) => ({
      category: row.category ?? "",
      totalCalls: toNumber(row.totalCalls ?? row.total_calls),
      avgBilledTokens: toNumber(row.avgBilledTokens ?? row.avg_billed_tokens),
      totalBilledTokens: toNumber(
        row.totalBilledTokens ?? row.total_billed_tokens
      ),
    }))
    .filter((row) => row.category !== "" && row.totalCalls > 0);
};

const FeatureRow = ({
  label,
  included,
}: {
  label: string;
  included: boolean;
}) => (
  <div className="flex items-center gap-2 text-sm">
    {included ? (
      <CheckCircleIcon className="h-5 w-5 text-emerald-500 flex-shrink-0" />
    ) : (
      <XCircleIcon className="h-5 w-5 text-gray-300 flex-shrink-0" />
    )}
    <span className={included ? "text-gray-700" : "text-gray-400"}>
      {label}
    </span>
  </div>
);

const DetailsNutritionPlatform = () => {
  const { setApiKey } = useApiKey();

  const [unauthorized, setUnauthorized] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [hideKey, setHideKey] = useState(true);
  const [productState, setProductState] = useState<ProductDataType>({
    isSubscribed: false,
    trialEnd: null,
    billingDate: new Date(),
    licenseKey: null,
    usageInfo: null,
    tiers: [],
  });

  useEffect(() => {
    if (productState?.licenseKey) {
      setApiKey(productState.licenseKey);
    }
  }, [productState?.licenseKey, setApiKey]);

  const [productSubDetail, setProductSubDetail] = useState<any>(null);
  const [autoRefillOnRenewal, setAutoRefillOnRenewal] = useState(0);
  const [autoRefillSaving, setAutoRefillSaving] = useState(false);
  const [predictedPercentage, setPredictedPercentage] = useState<number>(0);

  const calculatePredictedUsage = (
    startDate: number,
    endDate: number,
    currentUsage: number
  ): number => {
    const now = new Date();
    const start = new Date(startDate * 1000);
    const end = new Date(endDate * 1000);
    const totalDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    let daysPassed = (now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    daysPassed = Math.max(daysPassed, 1);
    if (daysPassed <= 0) return currentUsage;
    return (currentUsage / daysPassed) * totalDays;
  };

  useEffect(() => {
    if (productSubDetail?.stripeSubscription && productSubDetail.usageInfo) {
      const predicted = calculatePredictedUsage(
        productSubDetail.stripeSubscription.current_period_start,
        productSubDetail.stripeSubscription.current_period_end,
        productSubDetail.usageInfo.units
      );
      setPredictedPercentage(
        (predicted / productSubDetail.usageInfo.unitsQuota) * 100
      );
    }
  }, [productSubDetail]);

  const groupModes = [
    ["Request Type", "type"],
    ["Request Path", "path"],
    ["AI Mode", "aiMode"],
  ];
  const [usageDetailGroupMode, setUsageDetailGroupMode] = useState(
    groupModes[0]
  );
  const [usageView, setUsageView] = useState<"basic" | "graph" | "history">(
    "basic"
  );
  const [linksView, setLinksView] = useState<"API" | "SDK">("API");
  const [usageHistoryForPeriod, setUsageHistoryForPeriod] =
    useState<UsageHistoryReportForPeriod | null>();
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"account" | "easyai" | "insights">(
    "account"
  );
  const [showEasyAIFeatures, setShowEasyAIFeatures] = useState(true);
  const [showEasyAISignIn, setShowEasyAISignIn] = useState(true);
  const [isActivatingEasyAI, setIsActivatingEasyAI] = useState(false);
  const [easyAIActivationMessage, setEasyAIActivationMessage] = useState("");

  const userContext = useContext(UserContext);
  const [usageDetail, setUsageDetail] = useState<any>(null);
  const easyAIOwnerEmail = userContext?.email ?? "";
  const isNotSubscribedError = (err: AxiosError) =>
    err?.response?.status === 404 &&
    String(err?.response?.data?.cause || "")
      .toLowerCase()
      .includes("not subscribed");

  const patchLocalSubscriptionMetadata = (
    patch: Partial<{
      refillBehaviour: "auto" | "off";
      refillBatchQuantity: number;
      autoRefillOnRenewal: number;
    }>
  ) => {
    setProductSubDetail((prev: any) => {
      if (!prev?.stripeSubscription) return prev;
      const currentMd = prev?.stripeSubscription?.metadata || {};
      const nextMd = {
        ...currentMd,
        ...(patch.refillBehaviour !== undefined
          ? { refillBehaviour: patch.refillBehaviour }
          : {}),
        ...(patch.refillBatchQuantity !== undefined
          ? { refillBatchQuantity: String(patch.refillBatchQuantity) }
          : {}),
        ...(patch.autoRefillOnRenewal !== undefined
          ? {
              autoRefillOnRenewal: String(
                Math.max(0, patch.autoRefillOnRenewal)
              ),
            }
          : {}),
      };
      return {
        ...prev,
        stripeSubscription: {
          ...prev.stripeSubscription,
          metadata: nextMd,
        },
      };
    });
    if (patch.autoRefillOnRenewal !== undefined) {
      setAutoRefillOnRenewal(Math.max(0, patch.autoRefillOnRenewal));
    }
  };

  // Platform tier-based gating via subscription metadata
  const md = productSubDetail?.stripeSubscription?.metadata;
  const insightsUnlocked = md?.insightsAccess === "true";
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const aiInsightsUnlocked = md?.aiInsightsAccess === "true";
  const tierName = md?.tier ?? "";
  const currentTier = TIERS.find(
    (t) => t.name.toLowerCase() === tierName.toLowerCase()
  );
  const refillPricePerM = currentTier?.refillPricePerM ?? 25;
  const insightsLockedTooltip = insightsUnlocked
    ? undefined
    : "Insights are available on Growth and Pro plans.";

  const isDevEnv =
    process.env.NODE_ENV === "development" ||
    process.env.REACT_APP_ENV === "development" ||
    window.location.hostname === "localhost" ||
    window.location.hostname.includes("-dev");
  const easyAIBaseUrl = isDevEnv
    ? "https://easyai-dev.cloud.passiolife.com"
    : "https://easyai.passiolife.com";
  const easyAILoginUrl = `${easyAIBaseUrl}/login${
    easyAIOwnerEmail ? `?email=${encodeURIComponent(easyAIOwnerEmail)}` : ""
  }`;

  useEffect(() => {
    if (activeTab === "insights" && !insightsUnlocked) {
      setActiveTab("account");
    }
  }, [activeTab, insightsUnlocked]);

  // Data fetching - uses the "unified" endpoints since Platform shares the unified license
  const getProduct = async () =>
    await axios.get("/accounts/products/nutrition-platform/detail");
  const getProductUsageDetail = async () =>
    await axios.get(
      `/accounts/products/unified/usage/detail?groupMode=${usageDetailGroupMode[1]}&package=platform`
    );
  const getUsageHistoryDetail = async () =>
    await axios.get(
      `/accounts/products/unified/usage/history?periodStart=${billingPeriods[selectedPeriodIndex].start}&periodEnd=${billingPeriods[selectedPeriodIndex].end}&package=platform`
    );

  const [usedPercentage, setUsedPercentage] = useState(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [periodStart, setPeriodStart] = useState<number | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [periodEnd, setPeriodEnd] = useState<number | null>(null);
  const [billingPeriods, setBillingPeriods] = useState<
    { start: number; end: number }[]
  >([]);
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(0);

  const { isLoading: usageDetailLoading, isFetching: usageDetailFetching } =
    useQuery(
      ["platformUsageDetail", usageDetailGroupMode],
      getProductUsageDetail,
      {
        enabled: !!productSubDetail?.isSubscribed && !!userContext?.customerid,
        onError: (err: AxiosError) => {
          if (isNotSubscribedError(err)) return;
          error(
            err?.response?.data?.cause ||
              "Could not fetch the products usage detail"
          );
          if (err?.response?.status === 403) setUnauthorized(true);
        },
        onSuccess: (res: AxiosResponse) => setUsageDetail(res?.data),
      }
    );

  // [Platform Product] Global route averages for tier selection pricing card (all customers, cached 6h)
  const [globalRouteAverages, setGlobalRouteAverages] = useState<
    RouteAverageRow[]
  >([]);
  const { isLoading: globalAvgLoading, isFetching: globalAvgFetching } =
    useQuery(
      ["globalRouteAverages", GLOBAL_ROUTE_AVG_DAYS],
      () =>
        axios.get(
          `/accounts/products/nutrition-platform/usage/route-averages/global?days=${GLOBAL_ROUTE_AVG_DAYS}`
        ),
      {
        enabled: true,
        staleTime: 1000 * 60 * 60 * 6,
        cacheTime: 1000 * 60 * 60 * 12,
        refetchOnWindowFocus: false,
        retry: 1,
        onError: (err: AxiosError) => {
          // eslint-disable-next-line no-console
          console.warn(
            "Failed to fetch global route averages:",
            err?.response?.status,
            err?.response?.data
          );
        },
        onSuccess: (res: AxiosResponse) =>
          setGlobalRouteAverages(normalizeRouteAverageRows(res?.data)),
      }
    );
  const routeAverages = globalRouteAverages;
  const routeAvgLoading = globalAvgLoading;
  const routeAvgFetching = globalAvgFetching;

  const { isLoading: hisoryDataLoading, isFetching: historyDataFetching } =
    useQuery(
      ["platformUsageHistory", selectedPeriodIndex],
      getUsageHistoryDetail,
      {
        enabled: usageView === "history" && billingPeriods.length > 0,
        onError: (err: AxiosError) => {
          if (isNotSubscribedError(err)) return;
          error(err?.response?.data?.cause || "Could not fetch usage history");
          if (err?.response?.status === 403) setUnauthorized(true);
        },
        onSuccess: (res: AxiosResponse) => setUsageHistoryForPeriod(res?.data),
      }
    );

  const { isLoading, isFetching, refetch } = useQuery(
    ["platformProduct"],
    getProduct,
    {
      enabled: !!userContext?.customerid,
      onError: (err: AxiosError) => {
        error(err?.response?.data?.cause || "Could not fetch the products");
        if (err?.response?.status === 403) setUnauthorized(true);
      },
      onSuccess: (res: AxiosResponse) => {
        setProductSubDetail(res?.data);
        setUsedPercentage(
          res?.data?.usageInfo
            ? (res?.data?.usageInfo.units / res?.data?.usageInfo.unitsQuota) *
                100
            : 0
        );
        setProductState((prev) => ({
          ...prev,
          isSubscribed: res.data?.isSubscribed,
          trialEnd: res.data?.trialEnd ? new Date(res.data?.trialEnd) : null,
          billingDate: new Date(res.data?.billingDate),
          licenseKey: res.data?.licenseKey,
          usageInfo: res.data?.usageInfo,
          tiers: res.data?.tiers || [],
        }));
        // Populate auto-refill-on-renewal from metadata
        const renewalRefill = parseInt(
          res.data?.stripeSubscription?.metadata?.autoRefillOnRenewal ?? "0",
          10
        );
        setAutoRefillOnRenewal(renewalRefill || 0);
        const currentPeriodStart =
          res.data?.stripeSubscription?.current_period_start;
        const currentPeriodEnd =
          res.data?.stripeSubscription?.current_period_end;
        setPeriodStart(currentPeriodStart);
        setPeriodEnd(currentPeriodEnd);
        if (currentPeriodStart && currentPeriodEnd) {
          const periods = [];
          const periodLength = currentPeriodEnd - currentPeriodStart;
          const subscriptionStart = res.data.stripeSubscription.start_date;
          for (let i = 0; i < 6; i++) {
            const start = currentPeriodStart - i * periodLength;
            const end = currentPeriodEnd - i * periodLength;
            if (start >= subscriptionStart) periods.push({ start, end });
          }
          setBillingPeriods(periods);
        }
      },
    }
  );

  async function copyTextToClipboard(text: string) {
    return await navigator.clipboard.writeText(text);
  }
  const handleCopyClick = () => {
    copyTextToClipboard(productState?.licenseKey ?? "").then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 1500);
    });
  };

  const handleActivateEasyAI = async () => {
    setIsActivatingEasyAI(true);
    setEasyAIActivationMessage("");
    try {
      const res = await axios.post(
        "/accounts/products/unified/easyai/activate"
      );
      if (res?.data?.result?.status === "ok") {
        setEasyAIActivationMessage(
          "Activation requested. Check your email for a password setup link."
        );
      } else {
        const fallback =
          res?.data?.result?.response_body || "Activation failed";
        setEasyAIActivationMessage(fallback);
        error(fallback);
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.cause ||
        err?.response?.data?.message ||
        err?.message ||
        "Activation failed";
      setEasyAIActivationMessage(msg);
      error(msg);
    } finally {
      setIsActivatingEasyAI(false);
    }
  };

  const handleEasyAIResetPassword = async () => {
    await handleActivateEasyAI();
    const resetUrl = `${easyAIBaseUrl}/forgot-password${
      easyAIOwnerEmail ? `?email=${encodeURIComponent(easyAIOwnerEmail)}` : ""
    }`;
    window.open(resetUrl, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    const getQueryParam = (param: string) =>
      new URLSearchParams(window.location.search).get(param);
    const encodedData = getQueryParam("event");
    if (encodedData) {
      const decodedString = atob(encodedData);
      try {
        ReactGA.event(JSON.parse(decodedString));
      } catch (e) {
        /* ignore */
      }
    }
  }, []);

  const handleDownloadCSV = () => {
    const blob = new Blob([usageHistoryForPeriod?.usageCSV || ""], {
      type: "text/csv",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "usage_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (unauthorized) return <Navigate to="/access-restricted" />;

  // =========================================================================
  // NOT SUBSCRIBED - show tier pricing cards
  // Also show tier cards when user has no customerid (new user, query is disabled)
  // =========================================================================
  const queryDisabled = !userContext?.customerid;
  if (
    queryDisabled ||
    (!isFetching && !isLoading && !productState.isSubscribed)
  ) {
    return (
      <>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="font-bold text-2xl xl:text-3xl text-gray-900">
              Nutrition AI Platform
            </h2>
            <p className="text-gray-500 mt-1">
              API access, mobile SDKs, Easy AI, and more
            </p>
          </div>
        </div>
        <p className="text-gray-600 mb-8 text-lg">
          Choose the plan that fits your needs. All plans include Easy AI, SDK
          access, localization, and the full REST API.
        </p>
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {TIERS.map((tier) => (
            <Card
              key={tier.slug}
              className={`sm:p-6 flex flex-col relative ${
                tier.highlight
                  ? "ring-2 ring-indigo-500 shadow-lg"
                  : "border border-gray-200"
              }`}
            >
              {tier.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                  Most Popular
                </span>
              )}
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">{tier.name}</h3>
                <div className="mt-2">
                  <span className="text-4xl font-extrabold text-gray-900">
                    {tier.price}
                  </span>
                  <span className="text-gray-500 text-sm">/month</span>
                </div>
                <p className="mt-2 text-indigo-600 font-medium">
                  {tier.tokens} tokens included
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Refills at {tier.refillPrice}
                </p>
              </div>
              <div className="flex-1 space-y-3 mb-6">
                <FeatureRow
                  label="Easy AI + SDK Endpoint"
                  included={tier.features.easyAI}
                />
                <FeatureRow
                  label="Localization"
                  included={tier.features.localization}
                />
                <FeatureRow
                  label="Model Switching"
                  included={tier.features.modelSwitching}
                />
                <FeatureRow
                  label="Usage Insights"
                  included={tier.features.insights}
                />
                <FeatureRow
                  label="AI-Powered Insights"
                  included={tier.features.aiInsights}
                />
                <FeatureRow label={tier.features.support} included={true} />
              </div>
              <Link
                to={`/manage-subscription?product=${tier.slug}`}
                className={`block w-full text-center py-3 px-4 rounded-lg font-semibold transition-colors ${
                  tier.highlight
                    ? "bg-indigo-600 text-white hover:bg-indigo-700"
                    : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                }`}
              >
                Get Started
              </Link>
            </Card>
          ))}
        </div>

        {/* Estimated cost per call table */}
        <Card className="sm:p-6 mb-8">
          <p className="font-semibold text-gray-900 mb-1">
            Estimated Cost Per Call by Route Type
          </p>
          <p className="text-sm text-gray-500 mb-4">
            {globalRouteAverages.length > 0
              ? `Live platform-wide average token usage per API call over the last ${GLOBAL_ROUTE_AVG_DAYS} days.`
              : "Loading platform-wide average token usage from recent production requests."}
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-gray-500 text-xs uppercase tracking-wider">
                  <th className="py-2 pr-4">Route Type</th>
                  <th className="py-2 pr-4 text-right">Avg Tokens / Call</th>
                  {TIERS.map((t) => (
                    <th key={t.name} className="py-2 text-right">
                      {t.name}
                      <br />
                      <span className="text-[10px] normal-case font-normal">
                        ({t.refillPrice})
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {globalRouteAverages
                  .map((r) => ({
                    route: r.category,
                    avgTokens: Math.round(r.avgBilledTokens),
                  }))
                  .map((row) => (
                    <tr
                      key={row.route}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td className="py-2.5 pr-4 text-gray-900">{row.route}</td>
                      <td className="py-2.5 pr-4 text-right font-medium text-indigo-600">
                        {globalAvgLoading || globalAvgFetching
                          ? "..."
                          : `~${row.avgTokens.toLocaleString()}`}
                      </td>
                      {TIERS.map((t) => (
                        <td
                          key={t.name}
                          className="py-2.5 text-right text-gray-700"
                        >
                          {(
                            (row.avgTokens * t.refillPricePerM) /
                            1000000
                          ).toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                            minimumFractionDigits: 4,
                            maximumFractionDigits: 4,
                          })}
                        </td>
                      ))}
                    </tr>
                  ))}
                {!globalAvgLoading &&
                  !globalAvgFetching &&
                  globalRouteAverages.length === 0 && (
                    <tr>
                      <td
                        colSpan={2 + TIERS.length}
                        className="py-4 text-center text-sm text-gray-500"
                      >
                        No route-average data returned for the selected window.
                      </td>
                    </tr>
                  )}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300">
                  <td
                    colSpan={2 + TIERS.length}
                    className="pt-3 text-xs text-gray-400"
                  >
                    Costs shown are per single API call at each tier&apos;s
                    refill rate. Your included monthly tokens cover these calls
                    at no additional charge until exhausted.
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      </>
    );
  }

  // =========================================================================
  // SUBSCRIBED - full dashboard (mirrors Hub layout)
  // =========================================================================
  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-bold inter-bold text-md md:text-lg xl:text-3xl">
          Nutrition AI Platform Details
          {currentTier && (
            <span className="ml-3 text-sm font-medium bg-indigo-100 text-indigo-800 rounded-full px-3 py-1">
              {currentTier.name}
            </span>
          )}
        </h2>
      </div>

      {/* ===== Tabs ===== */}
      <div className="mb-6">
        <div className="flex items-end gap-2 border-b border-gray-200">
          <button
            onClick={() => setActiveTab("account")}
            className={`px-6 py-3 text-lg font-semibold rounded-t-lg border border-b-0 ${
              activeTab === "account"
                ? "bg-white text-indigo-700 border-gray-200"
                : "bg-gray-100 text-gray-600 border-transparent hover:text-indigo-600"
            }`}
          >
            Account Info
          </button>
          <button
            onClick={() => setActiveTab("easyai")}
            className={`px-6 py-3 text-lg font-semibold rounded-t-lg border border-b-0 relative ${
              activeTab === "easyai"
                ? "bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 border-indigo-200"
                : "bg-gradient-to-r from-gray-100 to-indigo-50 text-gray-700 border-transparent hover:text-indigo-600 hover:border-indigo-100"
            }`}
          >
            <span className="flex items-center gap-2">
              <SparklesIcon className="h-5 w-5 text-yellow-500" />
              Easy AI
              <span className="absolute -top-2 -right-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse shadow-lg">
                NEW
              </span>
            </span>
          </button>
          <span title={insightsLockedTooltip}>
            <button
              onClick={() => {
                if (insightsUnlocked) setActiveTab("insights");
              }}
              className={`px-6 py-3 text-lg font-semibold rounded-t-lg border border-b-0 ${
                activeTab === "insights"
                  ? "bg-white text-indigo-700 border-gray-200"
                  : "bg-gray-100 text-gray-600 border-transparent hover:text-indigo-600"
              } ${
                insightsUnlocked
                  ? ""
                  : "cursor-not-allowed text-gray-400 hover:text-gray-400"
              }`}
              aria-disabled={!insightsUnlocked}
            >
              <span className="inline-flex items-center gap-2">
                Insights
                {!insightsUnlocked && (
                  <span className="inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-600">
                    Locked
                  </span>
                )}
              </span>
            </button>
          </span>
        </div>
      </div>

      {/* ===== Account Info Tab ===== */}
      <div
        className={`lg:grid-cols-2 gap-8 auto-rows-auto ${
          activeTab === "account" ? "grid" : "hidden"
        }`}
      >
        {/* --- Usage Card --- */}
        <Card className="sm:p-6">
          <div className="flex justify-between">
            <div className="flex flex-col">
              <p className="font-medium">Current Period</p>
              <span className="text-sm text-gray-500">
                {new Date(
                  productSubDetail?.stripeSubscription?.current_period_start *
                    1000
                )?.toLocaleDateString("default", {
                  month: "2-digit",
                  year: "2-digit",
                  day: "2-digit",
                })}
                {"  -  "}
                {new Date(
                  productSubDetail?.stripeSubscription?.current_period_end *
                    1000
                )?.toLocaleDateString("default", {
                  month: "2-digit",
                  year: "2-digit",
                  day: "2-digit",
                })}
              </span>
            </div>
            {!isFetching && !isLoading ? (
              productState.trialEnd ? (
                <Badge isWarning>
                  <ClockIcon
                    className="h-5 w-5 flex-shrink-0 mr-2"
                    aria-hidden="true"
                  />
                  Free Trial Ends on{" "}
                  {productState.trialEnd.toLocaleDateString()}
                </Badge>
              ) : (
                <Badge>
                  <ClockIcon
                    className="h-5 w-5 flex-shrink-0 mr-2"
                    aria-hidden="true"
                  />
                  Billed Next on {productState.billingDate.toLocaleDateString()}
                </Badge>
              )
            ) : (
              <></>
            )}
          </div>
          <div className="flex flex-col gap-4 mt-4">
            <div className="flex flex-col justify-center items-center w-full">
              <div className="flex flex-col items-center w-full mb-2">
                <div className="flex space-x-2 mb-8">
                  {(["basic", "graph", "history"] as const).map((v) => (
                    <button
                      key={v}
                      onClick={() => setUsageView(v)}
                      className={`px-3 py-1 text-sm font-medium rounded-full ${
                        usageView === v
                          ? "bg-indigo-600 text-white"
                          : "bg-indigo-100 text-indigo-700 hover:bg-gray-300"
                      }`}
                    >
                      {v.charAt(0).toUpperCase() + v.slice(1)}
                    </button>
                  ))}
                </div>

                {isLoading || isFetching ? (
                  <Spinner className="!w-[2rem]" />
                ) : productSubDetail?.usageInfo?.units !== undefined ? (
                  <>
                    {/* ----- Basic View ----- */}
                    {usageView === "basic" && (
                      <>
                        <div className="text-passio-indigo text-md font-bold">
                          {convertToNumLetter(productSubDetail.usageInfo.units)}{" "}
                          /{" "}
                          {convertToNumLetter(
                            productSubDetail.usageInfo.unitsQuota
                          )}
                        </div>
                        <div className="w-full bg-indigo-50 rounded-full h-6 relative overflow-hidden">
                          <div
                            className="absolute bg-indigo-100 h-6 rounded-full"
                            style={{
                              width: `${Math.min(predictedPercentage, 100)}%`,
                            }}
                          ></div>
                          <div
                            className="absolute bg-indigo-600 h-6 rounded-full"
                            style={{
                              width: `${Math.min(usedPercentage, 100)}%`,
                            }}
                          ></div>
                        </div>
                        <div className="flex justify-between w-full text-indigo-600">
                          <span className="text-sm font-medium">
                            {usedPercentage.toFixed(1)}% Used (
                            {predictedPercentage.toFixed(1)}% Projected for
                            period)
                          </span>
                          {usedPercentage < 100 && (
                            <span className="text-sm font-medium">
                              {(100 - usedPercentage).toFixed(1)}% Available
                            </span>
                          )}
                        </div>
                      </>
                    )}

                    {/* ----- History View ----- */}
                    {usageView === "history" && (
                      <>
                        {!historyDataFetching && !hisoryDataLoading ? (
                          <div className="space-y-6">
                            <div className="flex justify-between items-end w-full">
                              <div className="w-1/2">
                                <label
                                  htmlFor="billing-period"
                                  className="block text-sm font-medium text-gray-700 mb-1"
                                >
                                  Select Billing Period
                                </label>
                                <select
                                  id="billing-period"
                                  className="w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                  value={selectedPeriodIndex}
                                  onChange={(e) =>
                                    setSelectedPeriodIndex(
                                      parseInt(e.target.value)
                                    )
                                  }
                                >
                                  {billingPeriods.map((period, index) => (
                                    <option key={index} value={index}>
                                      {new Date(
                                        period.start * 1000
                                      ).toLocaleDateString()}{" "}
                                      -{" "}
                                      {new Date(
                                        period.end * 1000
                                      ).toLocaleDateString()}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="w-1/2 px-8">
                                <Button
                                  onClick={handleDownloadCSV}
                                  className="whitespace-nowrap mb-1"
                                >
                                  Download Usage CSV
                                </Button>
                              </div>
                            </div>
                            <div className="mt-3">
                              <h3 className="text-sm font-medium text-gray-900 mb-2">
                                Invoice Downloads
                              </h3>
                              <ul className="divide-y divide-gray-200 rounded-md border border-gray-200">
                                {usageHistoryForPeriod?.invoiceLinks.map(
                                  (link, index) => (
                                    <li
                                      key={`inv-${index}`}
                                      className="px-4 py-2 hover:bg-gray-50"
                                    >
                                      <a
                                        href={link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-indigo-600 hover:text-indigo-800 flex items-center text-sm"
                                      >
                                        <ArrowDownTrayIcon className="pr-2 w-5 h-5" />
                                        Download Period Invoice {index + 1}
                                      </a>
                                    </li>
                                  )
                                )}
                              </ul>
                            </div>
                          </div>
                        ) : (
                          <Spinner className="!w-[2rem]" />
                        )}
                      </>
                    )}

                    {/* ----- Graph View ----- */}
                    {usageView === "graph" && (
                      <div className="w-full mt-4 items-center align-middle content-center">
                        <Plot
                          data={[
                            {
                              type: "bar" as const,
                              x: [
                                "Used",
                                "Remaining",
                                "Avg/Day",
                                "Projected Usage",
                                "Projected Overage",
                              ],
                              y: [
                                productSubDetail.usageInfo.units,
                                productSubDetail.usageInfo.unitsQuota -
                                  productSubDetail.usageInfo.units,
                                productSubDetail.usageInfo.units /
                                  ((new Date().getTime() -
                                    new Date(
                                      productSubDetail.stripeSubscription
                                        .current_period_start * 1000
                                    ).getTime()) /
                                    (1000 * 60 * 60 * 24)),
                                (predictedPercentage *
                                  productSubDetail.usageInfo.unitsQuota) /
                                  100,
                                Math.max(
                                  0,
                                  (predictedPercentage *
                                    productSubDetail.usageInfo.unitsQuota) /
                                    100 -
                                    productSubDetail.usageInfo.unitsQuota
                                ),
                              ],
                              text: [
                                convertToNumLetter(
                                  productSubDetail.usageInfo.units
                                ),
                                convertToNumLetter(
                                  productSubDetail.usageInfo.unitsQuota -
                                    productSubDetail.usageInfo.units
                                ),
                                convertToNumLetter(
                                  productSubDetail.usageInfo.units /
                                    ((new Date().getTime() -
                                      new Date(
                                        productSubDetail.stripeSubscription
                                          .current_period_start * 1000
                                      ).getTime()) /
                                      (1000 * 60 * 60 * 24))
                                ),
                                convertToNumLetter(
                                  (predictedPercentage *
                                    productSubDetail.usageInfo.unitsQuota) /
                                    100
                                ),
                                convertToNumLetter(
                                  Math.max(
                                    0,
                                    (predictedPercentage *
                                      productSubDetail.usageInfo.unitsQuota) /
                                      100 -
                                      productSubDetail.usageInfo.unitsQuota
                                  )
                                ),
                              ],
                              textposition: "auto" as const,
                              hoverinfo: "text" as const,
                              marker: {
                                color: [
                                  "#4F46E5",
                                  "#818CF8",
                                  "#A5B4FC",
                                  "#C7D2FE",
                                  "#a42820",
                                ],
                              },
                              yaxis: "y" as const,
                            },
                            {
                              type: "bar" as const,
                              x: ["Days Left"],
                              y: [
                                Math.ceil(
                                  (new Date(
                                    productSubDetail.stripeSubscription
                                      .current_period_end * 1000
                                  ).getTime() -
                                    new Date().getTime()) /
                                    (1000 * 60 * 60 * 24)
                                ),
                              ],
                              text: [
                                Math.ceil(
                                  (new Date(
                                    productSubDetail.stripeSubscription
                                      .current_period_end * 1000
                                  ).getTime() -
                                    new Date().getTime()) /
                                    (1000 * 60 * 60 * 24)
                                ).toString() + " days",
                              ],
                              textposition: "auto" as const,
                              hoverinfo: "text" as const,
                              marker: { color: "#10B981" },
                              yaxis: "y2" as const,
                            },
                          ]}
                          layout={{
                            title: "Token Usage Overview",
                            font: { size: 10 },
                            autosize: true,
                            height: 300,
                            margin: { l: 40, r: 40, t: 40, b: 80 },
                            bargap: 0.15,
                            yaxis: {
                              title: "Tokens",
                              side: "left" as const,
                              range: [
                                0,
                                Math.max(
                                  productSubDetail.usageInfo.unitsQuota,
                                  (predictedPercentage *
                                    productSubDetail.usageInfo.unitsQuota) /
                                    100
                                ),
                              ],
                            },
                            yaxis2: {
                              title: "Billing Period Days",
                              side: "right" as const,
                              overlaying: "y" as const,
                              rangemode: "tozero" as const,
                              range: [
                                0,
                                Math.max(
                                  31,
                                  Math.ceil(
                                    (new Date(
                                      productSubDetail.stripeSubscription
                                        .current_period_end * 1000
                                    ).getTime() -
                                      new Date().getTime()) /
                                      (1000 * 60 * 60 * 24)
                                  )
                                ),
                              ],
                              dtick: 5,
                            },
                            showlegend: false,
                          }}
                          useResizeHandler={true}
                          style={{ width: "auto", height: "220px" }}
                          config={{ displayModeBar: false, responsive: true }}
                        />
                      </div>
                    )}

                    {/* Overage Warning */}
                    <div className="text-sm text-gray-500 mt-2 mb-2 w-full">
                      {predictedPercentage > 100 && (
                        <div className="mt-2 p-4 bg-red-100 rounded-md text-red-600">
                          <p>
                            Warning: You are projected to exceed your token
                            quota.
                          </p>
                          <p>
                            {productSubDetail.stripeSubscription.metadata
                              .refillBehaviour === "auto"
                              ? `Auto-refill will purchase more tokens at $${refillPricePerM} per million.`
                              : "Consider enabling auto-refill or manually purchasing more tokens to avoid service interruption."}
                          </p>
                          <p className="pt-2 font-bold">
                            {`Estimated Overage Cost: ${(
                              refillPricePerM *
                              Math.ceil(
                                Math.max(
                                  0,
                                  (predictedPercentage *
                                    productSubDetail.usageInfo.unitsQuota) /
                                    100 -
                                    productSubDetail.usageInfo.unitsQuota
                                ) / 1000000
                              )
                            ).toLocaleString("en-US", {
                              currency: "USD",
                              style: "currency",
                            })}`}
                          </p>
                        </div>
                      )}
                    </div>
                    <span className="text-xs mt-2 text-gray-800">
                      *Usage and predictions in this dashboard may take a few
                      minutes to reflect the latest data. Your API client
                      responses will be up-to-date.
                    </span>
                  </>
                ) : (
                  "-"
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* --- API Key & Links Card --- */}
        <Card className="sm:p-6 row-span-2">
          <div className="flex flex-col">
            <p className="font-medium">API Key & Info</p>
          </div>
          <div className="mt-6">
            <dt className="text-sm font-medium text-gray-500">API Key</dt>
            <dd className="mt-1 text-sm text-gray-900">
              <ul
                role="list"
                className="divide-y divide-gray-200 rounded-md border border-gray-200"
              >
                <li className="flex items-center justify-between py-3 pl-3 pr-4 text-sm">
                  <div className="flex w-0 flex-1 items-center">
                    <KeyIcon
                      className="h-5 w-5 flex-shrink-0 text-gray-400"
                      aria-hidden="true"
                    />
                    <span className="ml-2 w-0 flex-1 truncate">
                      {hideKey
                        ? Array.from({
                            length: productState?.licenseKey?.length || 0,
                          }).map((_, i) => <span key={i}>&#8226;</span>)
                        : productState?.licenseKey ?? ""}
                    </span>
                  </div>
                  <div className="ml-4 flex flex-row flex-shrink items-center space-x-2">
                    {isCopied ? (
                      <ClipboardDocumentCheckIcon className="h-5 w-5 flex-shrink-0 text-green-700" />
                    ) : (
                      <DocumentDuplicateIcon
                        className="h-5 w-5 flex-shrink-0 text-passio-indigo cursor-pointer"
                        onClick={handleCopyClick}
                      />
                    )}
                    {hideKey ? (
                      <EyeSlashIcon
                        className="h-5 w-5 flex-shrink-0 text-passio-indigo cursor-pointer"
                        onClick={() => setHideKey(false)}
                      />
                    ) : (
                      <EyeIcon
                        className="h-5 w-5 flex-shrink-0 text-passio-indigo cursor-pointer"
                        onClick={() => setHideKey(true)}
                      />
                    )}
                  </div>
                </li>
                <li className="flex items-center justify-between py-3 pl-3 pr-4 text-sm">
                  <div className="flex w-0 flex-1 items-center">
                    <ArrowTopRightOnSquareIcon className="h-5 w-5 flex-shrink-0 text-gray-400" />
                    <span className="ml-2 w-0 flex-1 truncate">
                      <Link
                        to="/tools/mealplans"
                        className="font-medium text-indigo-600 hover:text-indigo-500"
                      >
                        Meal Planning Tool
                      </Link>
                    </span>
                  </div>
                </li>
              </ul>
              <p className="text-gray-500 text-sm ml-4 mt-1">
                * Keep your API Key safe - see the best practices in the
                documentation.
              </p>
            </dd>
          </div>
          <div className="pt-8">
            <div className="pb-2 flex flex-grow justify-center gap-3 items-center">
              {(["API", "SDK"] as const).map((v) => (
                <button
                  key={v}
                  onClick={() => setLinksView(v)}
                  className={`px-3 py-1 text-sm font-medium rounded-full ${
                    linksView === v
                      ? "bg-indigo-600 text-white"
                      : "bg-indigo-100 text-indigo-700 hover:bg-gray-300"
                  }`}
                >
                  {v} Links
                </button>
              ))}
            </div>
            <dd className="mt-1 text-sm text-gray-900">
              {linksView === "API" ? (
                <ul
                  role="list"
                  className="divide-y divide-gray-200 rounded-md border border-gray-200"
                >
                  {[
                    ["https://passio.gitbook.io/nutrition-ai", "Documentation"],
                    [
                      "https://passio.gitbook.io/nutrition-ai/rest-api/api-overview/authorization-setup",
                      "Authorize With Your API Key",
                    ],
                    [
                      "https://api.passiolife.com/v2/products/docs",
                      "Food Database REST OpenAPI Documentation",
                    ],
                  ].map(([href, label]) => (
                    <li
                      key={label}
                      className="flex items-center justify-between py-3 pl-3 pr-4 text-sm"
                    >
                      <div className="flex w-0 flex-1 items-center">
                        <ArrowTopRightOnSquareIcon className="h-5 w-5 flex-shrink-0 text-gray-400" />
                        <span className="ml-2 w-0 flex-1 truncate">
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-indigo-600 hover:text-indigo-500"
                          >
                            {label}
                          </a>
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <ul
                  role="list"
                  className="divide-y divide-gray-200 rounded-md border border-gray-200"
                >
                  {[
                    ["https://passio.gitbook.io/nutrition-ai", "Documentation"],
                    [
                      "https://github.com/Passiolife/Passio-Nutrition-AI-iOS-SDK-Distribution",
                      "iOS SDK Github Repository",
                    ],
                    [
                      "https://github.com/Passiolife/Passio-Nutrition-AI-Android-SDK-Distribution",
                      "Android SDK Github Repository",
                    ],
                    [
                      "https://github.com/Passiolife/Passio-Nutrition-AI-RN-SDK-Distribution",
                      "React Native SDK Github Repository",
                    ],
                    ["https://pub.dev/packages/nutrition_ai", "Flutter Plugin"],
                  ].map(([href, label]) => (
                    <li
                      key={label}
                      className="flex items-center justify-between py-3 pl-3 pr-4 text-sm"
                    >
                      <div className="flex w-0 flex-1 items-center">
                        <ArrowTopRightOnSquareIcon className="h-5 w-5 flex-shrink-0 text-gray-400" />
                        <span className="ml-2 w-0 flex-1 truncate">
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-indigo-600 hover:text-indigo-500"
                          >
                            {label}
                          </a>
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </dd>
          </div>
          {linksView === "API" && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start">
                <ChatBubbleLeftRightIcon className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Need Help with Integration?
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Chat with our AI assistant for help with REST API
                    authorization, usage, code examples, and more.
                  </p>
                  <button
                    onClick={() => setIsChatbotOpen(true)}
                    className="mt-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
                  >
                    <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
                    Open Integration Assistant
                  </button>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* --- Refill Settings Card --- */}
        <div className="flex-grow">
          {(!!isLoading && !!isFetching) || !!productSubDetail ? (
            <RefillSettingsCard
              subscriptionMetadata={ConvertToSubscriptionMetadata(
                productSubDetail?.stripeSubscription?.metadata ||
                  ({} as SubscriptionMetadata)
              )}
              refillPricePerMillion={currentTier?.refillPricePerM}
              onRefill={async () => {
                await axios.post(
                  "/accounts/products/nutrition-platform/subscription/refill"
                );
                await new Promise((resolve) => setTimeout(resolve, 2000));
                refetch().catch(() => {});
              }}
              onSave={async (newMetadata: SubscriptionMetadata) => {
                const payload = {
                  refillBehaviour: newMetadata.refillBehaviour,
                  refillBatchQuantity: newMetadata.refillBatchQuantity,
                  autoRefillOnRenewal,
                };
                await axios.put(
                  "/accounts/products/nutrition-platform/subscription",
                  payload
                );
                patchLocalSubscriptionMetadata(payload);
                refetch().catch(() => {});
                setTimeout(() => {
                  refetch().catch(() => {});
                }, 1500);
              }}
            />
          ) : (
            <Spinner />
          )}
        </div>

        {/* --- Auto-Refill on Renewal Card --- */}
        {currentTier && productSubDetail && (
          <Card className="sm:p-6">
            <h3 className="text-base font-semibold mb-1">
              Pre-Purchase Tokens on Renewal
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Automatically buy extra tokens each time your subscription renews,
              so you start each month with a higher balance. These are purchased
              at your tier&apos;s refill rate on top of your included tokens.
            </p>
            <div className="space-y-3">
              <div>
                <label
                  htmlFor="autoRefillOnRenewal"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Refill units to pre-purchase each renewal
                </label>
                <div className="flex items-end">
                  <input
                    type="number"
                    id="autoRefillOnRenewal"
                    value={autoRefillOnRenewal}
                    onChange={(e) =>
                      setAutoRefillOnRenewal(
                        Math.max(0, parseInt(e.target.value) || 0)
                      )
                    }
                    className="w-24 px-3 py-2 text-sm rounded-md border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                  />
                  <span className="text-sm pl-2 whitespace-nowrap text-gray-600">
                    M tokens
                  </span>
                </div>
              </div>

              {/* Monthly cost breakdown */}
              <div className="p-3 bg-gray-50 rounded-lg text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subscription</span>
                  <span className="font-medium text-gray-900">
                    {currentTier.price}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Included tokens</span>
                  <span className="font-medium text-gray-900">
                    {currentTier.tokens}
                  </span>
                </div>
                {autoRefillOnRenewal > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      Pre-purchased refills ({autoRefillOnRenewal}M &times;{" "}
                      {currentTier.refillPrice})
                    </span>
                    <span className="font-medium text-indigo-600">
                      +$
                      {(
                        autoRefillOnRenewal * currentTier.refillPricePerM
                      ).toFixed(2)}
                    </span>
                  </div>
                )}
                <hr className="my-1" />
                <div className="flex justify-between font-semibold">
                  <span className="text-gray-900">Total per month</span>
                  <span className="text-gray-900">
                    $
                    {(
                      currentTier.priceNum +
                      autoRefillOnRenewal * currentTier.refillPricePerM
                    ).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
                {autoRefillOnRenewal > 0 && (
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Total tokens at renewal</span>
                    <span>
                      {currentTier.tokens.replace("M", "")}M +{" "}
                      {autoRefillOnRenewal}M ={" "}
                      {parseInt(currentTier.tokens) + autoRefillOnRenewal}M
                    </span>
                  </div>
                )}
              </div>

              <button
                onClick={async () => {
                  setAutoRefillSaving(true);
                  try {
                    const refillBehaviour =
                      productSubDetail?.stripeSubscription?.metadata
                        ?.refillBehaviour ?? "auto";
                    const refillBatchQuantity = parseInt(
                      productSubDetail?.stripeSubscription?.metadata
                        ?.refillBatchQuantity ?? "1",
                      10
                    );
                    const payload = {
                      refillBehaviour,
                      refillBatchQuantity,
                      autoRefillOnRenewal,
                    };
                    await axios.put(
                      "/accounts/products/nutrition-platform/subscription",
                      payload
                    );
                    patchLocalSubscriptionMetadata(payload);
                    success("Renewal pre-purchase settings saved");
                    refetch().catch(() => {});
                    setTimeout(() => {
                      refetch().catch(() => {});
                    }, 1500);
                  } catch (err: any) {
                    error(
                      err?.response?.data?.cause || "Failed to save settings"
                    );
                  } finally {
                    setAutoRefillSaving(false);
                  }
                }}
                disabled={autoRefillSaving}
                className="w-full py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {autoRefillSaving ? "Saving..." : "Save Renewal Settings"}
              </button>
            </div>
          </Card>
        )}

        {/* --- Usage Detail Card --- */}
        <div className="w-full col-span-2 h-min">
          {!usageDetailFetching && !usageDetailLoading && usageDetail ? (
            <UsageDetailCard
              usageDetail={usageDetail}
              usageDetailGroupMode={usageDetailGroupMode}
              setUsageDetailGroupMode={setUsageDetailGroupMode}
              groupModes={groupModes}
            />
          ) : (
            <Card>
              <Spinner />
            </Card>
          )}
        </div>

        {/* --- Pricing Estimate Card (live global route averages) --- */}
        <Card className="sm:p-6 col-span-1 lg:col-span-2">
          <div className="flex justify-between items-center mb-4">
            <div>
              <p className="font-medium">Pricing Estimate by Route Type</p>
              <span className="text-sm text-gray-500">
                Platform-wide average billed tokens per call over the last{" "}
                {GLOBAL_ROUTE_AVG_DAYS} days
              </span>
            </div>
            {currentTier && (
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                Refill rate: {currentTier.refillPrice}
              </span>
            )}
          </div>
          {routeAvgLoading || routeAvgFetching ? (
            <Spinner className="!w-[2rem]" />
          ) : routeAverages.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-500 text-xs uppercase tracking-wider">
                    <th className="py-2 pr-4">Route Category</th>
                    <th className="py-2 pr-4 text-right">Avg Tokens / Call</th>
                    <th className="py-2 pr-4 text-right">
                      Total Calls ({GLOBAL_ROUTE_AVG_DAYS}d)
                    </th>
                    <th className="py-2 pr-4 text-right">
                      Total Tokens ({GLOBAL_ROUTE_AVG_DAYS}d)
                    </th>
                    {currentTier && (
                      <th className="py-2 text-right">Est. Cost / 1K Calls</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {routeAverages.map((row) => (
                    <tr
                      key={row.category}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td className="py-3 pr-4 font-medium text-gray-900">
                        {row.category}
                      </td>
                      <td className="py-3 pr-4 text-right text-indigo-600 font-semibold">
                        {convertToNumLetter(Math.round(row.avgBilledTokens))}
                      </td>
                      <td className="py-3 pr-4 text-right text-gray-700">
                        {row.totalCalls.toLocaleString()}
                      </td>
                      <td className="py-3 pr-4 text-right text-gray-700">
                        {convertToNumLetter(Math.round(row.totalBilledTokens))}
                      </td>
                      {currentTier && (
                        <td className="py-3 text-right font-semibold text-gray-900">
                          {(
                            (row.avgBilledTokens *
                              1000 *
                              currentTier.refillPricePerM) /
                            1000000
                          ).toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-300 font-semibold text-gray-900">
                    <td className="py-3 pr-4">
                      Total ({GLOBAL_ROUTE_AVG_DAYS} days)
                    </td>
                    <td className="py-3 pr-4 text-right">-</td>
                    <td className="py-3 pr-4 text-right">
                      {routeAverages
                        .reduce((s, r) => s + r.totalCalls, 0)
                        .toLocaleString()}
                    </td>
                    <td className="py-3 pr-4 text-right">
                      {convertToNumLetter(
                        Math.round(
                          routeAverages.reduce(
                            (s, r) => s + r.totalBilledTokens,
                            0
                          )
                        )
                      )}
                    </td>
                    {currentTier && (
                      <td className="py-3 text-right">
                        {(
                          (routeAverages.reduce(
                            (s, r) => s + r.totalBilledTokens,
                            0
                          ) *
                            currentTier.refillPricePerM) /
                          1000000
                        ).toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </td>
                    )}
                  </tr>
                </tfoot>
              </table>
              <p className="text-xs text-gray-400 mt-3">
                Based on platform-wide usage from the last{" "}
                {GLOBAL_ROUTE_AVG_DAYS} days. &quot;Est. Cost / 1K Calls&quot;
                shows what 1,000 calls of that type would cost at your
                tier&apos;s refill rate. Included monthly tokens are not
                factored into the cost estimate.
              </p>
            </div>
          ) : (
            <p className="text-gray-400 text-sm">
              No usage data from the last {GLOBAL_ROUTE_AVG_DAYS} days to
              estimate pricing.
            </p>
          )}
        </Card>

        {/* --- Support Card --- */}
        <Card className="sm:p-6">
          <div>
            <p className="font-medium">Support & Documentation</p>
            <span className="text-sm text-gray-500">
              Links for additional support
            </span>
          </div>
          {[
            ["Documentation", "https://passio.gitbook.io/nutrition-ai"],
            ["Email Support", "mailto:support@passiolife.com"],
            [
              "Slack Support",
              "https://passio-community.slack.com/channels/help",
            ],
            [
              "Roadmap",
              "https://portal.productboard.com/passio/1-nutrition-ai/tabs/1-recently-launched",
            ],
          ].map(([label, href]) => (
            <div className="mt-4" key={label}>
              <dt className="text-sm font-medium text-gray-500">{label}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-indigo-600 hover:text-indigo-500"
                >
                  {href.replace("mailto:", "")}
                </a>
              </dd>
            </div>
          ))}
        </Card>
        <SlackSignUp />
      </div>

      {/* ===== Easy AI Tab ===== */}
      <div className={activeTab === "easyai" ? "block" : "hidden"}>
        <Card className="sm:p-0 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50">
            <div className="flex items-center gap-2">
              <SparklesIcon className="h-5 w-5 text-indigo-600" />
              <p className="font-semibold text-indigo-900">Easy AI Builder</p>
              <span className="bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                NEW
              </span>
            </div>
            <a
              href={easyAILoginUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500"
            >
              Open in new tab
              <ArrowTopRightOnSquareIcon className="ml-2 h-4 w-4" />
            </a>
          </div>
          <div className="m-4 rounded-lg border border-indigo-200 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 overflow-hidden">
            <button
              onClick={() => setShowEasyAIFeatures(!showEasyAIFeatures)}
              className="w-full p-3 flex items-center justify-between hover:bg-indigo-100/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <SparklesIcon className="h-5 w-5 text-yellow-500" />
                <span className="font-semibold text-indigo-900">
                  Create Custom AI Functions in Seconds
                </span>
              </div>
              {showEasyAIFeatures ? (
                <ChevronUpIcon className="h-5 w-5 text-indigo-600" />
              ) : (
                <ChevronDownIcon className="h-5 w-5 text-indigo-600" />
              )}
            </button>
            {showEasyAIFeatures && (
              <div className="px-4 pb-4">
                <p className="text-sm text-gray-700 mb-3">
                  If we don&apos;t have the AI function you need, build it
                  yourself! No coding or ML expertise required.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  {[
                    ["Any Media Input", "Text, images, audio, video, PDFs"],
                    ["Custom Chatbots", "Train on your own knowledge base"],
                    ["Any AI Model", "GPT-4o, Claude, Gemini, & more"],
                    ["Chain Functions", "Run multiple AI steps in sequence"],
                  ].map(([title, desc]) => (
                    <div
                      key={title}
                      className="bg-white/70 rounded-md p-2 border border-indigo-100"
                    >
                      <p className="font-medium text-indigo-800">{title}</p>
                      <p className="text-xs text-gray-600">{desc}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-3 italic">
                  Instantly get production-ready API routes for your custom
                  functions. Usage counts toward your Platform token budget.
                </p>
              </div>
            )}
          </div>
          <div className="mx-4 mb-4 rounded-lg border border-amber-200 bg-amber-50 overflow-hidden">
            <button
              onClick={() => setShowEasyAISignIn(!showEasyAISignIn)}
              className="w-full p-3 flex items-center justify-between hover:bg-amber-100/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <InformationCircleIcon className="h-5 w-5 text-amber-600" />
                <span className="font-semibold text-amber-900">
                  Sign In with Your Passio Account
                </span>
              </div>
              {showEasyAISignIn ? (
                <ChevronUpIcon className="h-5 w-5 text-amber-600" />
              ) : (
                <ChevronDownIcon className="h-5 w-5 text-amber-600" />
              )}
            </button>
            {showEasyAISignIn && (
              <div className="px-4 pb-4">
                <p className="text-sm text-amber-800 mb-2">
                  Use the <strong>same email</strong> as your current account:{" "}
                  <span className="font-mono bg-amber-100 px-1 rounded">
                    {easyAIOwnerEmail || "your email"}
                  </span>
                </p>
                <p className="text-sm text-amber-800 mb-3">
                  Your password is the one you set when you received the{" "}
                  <strong>password reset email</strong> from Passio. If you
                  haven&apos;t set one yet, check your inbox or click below.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={handleEasyAIResetPassword}
                    className="inline-flex items-center px-3 py-1.5 bg-amber-600 text-white text-sm font-medium rounded-md hover:bg-amber-700 transition-colors"
                  >
                    <EnvelopeIcon className="h-4 w-4 mr-1.5" />
                    Send Password Reset Email
                  </button>
                  <span className="text-xs text-amber-700">
                    Email fields below are pre-filled with your account email.
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className="mx-4 mb-4 rounded-lg border border-indigo-200 bg-indigo-50 p-4">
            <div className="flex items-start gap-3">
              <SparklesIcon className="h-5 w-5 text-indigo-600 mt-0.5" />
              <div className="flex-1">
                <h5 className="font-semibold text-indigo-900 mb-1">
                  Included Easy AI Subscription
                </h5>
                <p className="text-sm text-indigo-800 mb-3">
                  Your plan includes Easy AI. If you haven&apos;t activated it
                  yet, click below to provision your account and trigger the
                  password setup email.
                </p>
                <button
                  onClick={handleActivateEasyAI}
                  disabled={isActivatingEasyAI}
                  className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-md hover:bg-indigo-700 disabled:opacity-60"
                >
                  {isActivatingEasyAI
                    ? "Activating..."
                    : "Activate Included Easy AI Subscription"}
                </button>
                {easyAIActivationMessage && (
                  <p className="mt-2 text-xs text-indigo-700">
                    {easyAIActivationMessage}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="h-[75vh]">
            <iframe
              src={easyAILoginUrl}
              className="w-full h-full border-0"
              title="EasyAI"
            />
          </div>
        </Card>
      </div>

      {/* ===== Insights Tab (gated by insightsAccess metadata) ===== */}
      <div className={activeTab === "insights" ? "block" : "hidden"}>
        {insightsUnlocked ? (
          <RouteInsights onOpenEasyAI={() => setActiveTab("easyai")} />
        ) : (
          <Card className="sm:p-6 text-center">
            <p className="text-gray-500 text-lg">
              Insights are available on Growth and Pro plans.
            </p>
            <p className="text-gray-400 text-sm mt-2">
              Upgrade your subscription to unlock route analytics and usage
              insights.
            </p>
          </Card>
        )}
      </div>

      {/* ===== Chatbot Overlay ===== */}
      {isChatbotOpen && (
        <div className="fixed inset-y-0 right-0 w-[550px] bg-white shadow-2xl z-50 flex flex-col border-l border-gray-200">
          <div className="flex items-center justify-between px-4 py-3 bg-indigo-600 text-white">
            <div className="flex items-center">
              <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
              <h2 className="text-lg font-semibold">Integration Assistant</h2>
            </div>
            <button
              onClick={() => setIsChatbotOpen(false)}
              className="p-1 hover:bg-indigo-700 rounded-md transition-colors"
              aria-label="Close chat"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="flex-1 overflow-hidden">
            <iframe
              src="https://easyai.cloud-prod.passiolife.com/embed/chatbot?chatbotId=6453e3ba-df9a-4d1e-8a05-2800bcdb0997&apiKey=ZT3ift4W1-d7IoOz_x3MQMng6yZocy-2UAXaz62AtEY&customerTag=easyaitest"
              className="w-full h-full border-0"
              title="Integration Assistant Chatbot"
            />
          </div>
          <div className="px-4 py-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-t border-gray-200">
            <div className="text-center mb-3">
              <p className="text-sm text-gray-700 mb-2 font-medium">
                This chatbot was created in seconds using
              </p>
              <a
                href="https://easyai.cloud-prod.passiolife.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-sm font-semibold rounded-lg hover:from-indigo-700 hover:to-blue-700 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                Passio EasyAI
                <ArrowTopRightOnSquareIcon className="ml-2 h-4 w-4" />
              </a>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              Create your own AI chatbots with custom knowledge from any file
              type, custom functions, and image understanding. All API routes
              are hosted and production-ready within 30 seconds.
            </p>
          </div>
        </div>
      )}
      {isChatbotOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-40 lg:hidden"
          onClick={() => setIsChatbotOpen(false)}
        />
      )}
    </>
  );
};

export default DetailsNutritionPlatform;
